// Tribute Window lifecycle (docs/04 §8): start, end, pay (early), fail → Consumed (§19).
// Reckonings (§16) attach a Collector rule to every 5th window.
import { B } from '../content/balance';
import { CHARMS } from '../content/registry';
import { D } from './decimal';
import { say, tip } from './dialogue';
import { buildStrips } from './grid';
import { doomWindowEnd } from './doom';
import { moodWindowEnd, moodWindowStart } from './hunger';
import { afterTribute } from './ending';
import { addShard } from './meta';
import { computeModifiers, type Modifiers } from './modifiers';
import { announceReckoning, reckoningDef, rollNextRule, setupReckoning, splitRule } from './reckoning';
import { stream } from './rng';
import { consumedBonus, earlyBonus, par, soulsForTribute, spinBudget, tribute } from './tribute';
import type { GameEvent, GameState, RunSummary } from './types';

export function startWindow(state: GameState, baseMods: Modifiers, n: number, events: GameEvent[]): void {
  const run = state.run!;
  const firstWindow = run.window.n === 0;
  const carry = Math.min(run.charges.hold, baseMods.holdCarryMax);
  run.window = { n, tribute: D(0), spinsLeft: 0, spinsTotal: 0, spinsUsed: 0, counters: {} };
  run.hexes = run.hexes ?? [];
  const reckMult = setupReckoning(state, baseMods, n);
  // The collector's rule shapes this window's own budget (Stopwatch), so recompute.
  const mods = computeModifiers(state.meta, run);
  let t = tribute(n, mods, reckMult);
  if (run.flags.loanNext) {
    t = t.mul(run.flags.loanNext).ceil();
    delete run.flags.loanNext;
  }
  const spins = Math.max(1, spinBudget(n, mods, run.nextWindow.spins));
  run.window.tribute = t;
  run.window.spinsLeft = spins;
  run.window.spinsTotal = spins;
  run.charges = {
    hold: mods.features.has('hold') ? mods.holdPerWindow + run.nextWindow.hold + (firstWindow ? 0 : carry) : 0,
    nudge: mods.features.has('nudge') ? mods.nudgePerWindow + run.nextWindow.nudge : 0,
  };
  run.nextWindow = { spins: 0, hold: 0, nudge: 0 };
  const adoring = moodWindowStart(state, mods, events);
  run.charges.hold += adoring.hold;
  run.charges.nudge += adoring.nudge;
  if (!firstWindow && mods.compoundPct > 0 && run.coins.gt(0)) run.coins = run.coins.add(run.coins.mul(mods.compoundPct).floor());
  if (mods.windowStartCoinsPct > 0) {
    const tip = t.mul(mods.windowStartCoinsPct).floor();
    if (tip.gt(0)) {
      run.coins = run.coins.add(tip);
      events.push({ e: 'CHARM_TRIGGER', id: 'tipjar', kind: 'coins', amount: tip.toString() });
    }
  }
  buildStrips(run, stream(run.rng, 'reels'));
  run.held = run.stops.map(() => false);
  run.freeHold = run.stops.map(() => false);
  run.holdPaid = run.stops.map(() => 0);
  run.eaten = [];
  run.phase = 'ready';
  run.spin = null;
  run.intermission = null;
  run.bonus = null;
  run.gamble = null;
  if (run.nightShift) run.nightShift.wild = Math.min(run.nightShift.wild, run.stops.length - 1);
  events.push({
    e: 'WINDOW_START',
    n,
    tribute: t.toString(),
    spins,
    hold: run.charges.hold,
    nudge: run.charges.nudge,
    par: par(t, spins).toString(),
  });
  if (run.window.reckoning) announceReckoning(state, events);
  else say(state, 'windowStart', events, { n, due: t.toString() });
  if (run.charges.hold > 0) tip(state, 'hold', events);
}

/** The Twins: the first half of the tribute is due at half the spins. Returns false if Consumed. */
function twinsCheckpoint(state: GameState, events: GameEvent[]): boolean {
  const run = state.run!;
  const w = run.window;
  if (!splitRule(run) || w.counters.twinsPaid || !w.counters.twinsAt || w.spinsUsed < w.counters.twinsAt) return true;
  const half = w.tribute.div(2).ceil();
  if (run.coins.lt(half)) {
    consume(state, 'fail', events);
    return false;
  }
  run.coins = run.coins.sub(half);
  w.tribute = w.tribute.sub(half);
  w.counters.twinsPaid = 1;
  events.push({ e: 'TWINS_PAID', half: half.toString(), coinsLeft: run.coins.toString() });
  say(state, 'twins', events, undefined, { force: true });
  return true;
}

/** Called after every banked spin or finished bonus. */
export function afterSpin(state: GameState, mods: Modifiers, events: GameEvent[]): void {
  const run = state.run!;
  run.phase = 'ready';
  if (!twinsCheckpoint(state, events)) return;
  const nightLeft = run.nightShift?.left ?? 0;
  if (run.coins.gte(run.window.tribute) && run.window.spinsLeft > 0) tip(state, 'payNow', events);
  if (run.window.spinsLeft === 1 && !nightLeft) say(state, 'lastSpin', events);
  if (run.window.spinsLeft <= 0 && nightLeft <= 0) return windowEnd(state, mods, events, 0);
  rollNextRule(state, events);
}

function runWindowEndCharms(state: GameState, mods: Modifiers, events: GameEvent[]): void {
  const run = state.run!;
  for (const id of run.charms.slice(0, mods.charmSlots)) {
    const def = CHARMS.get(id);
    if (!def?.onWindowEnd) continue;
    const coins = def.onWindowEnd({
      take: (key) => {
        const k = `${id}.${key}`;
        const v = run.charmBank[k] ?? D(0);
        delete run.charmBank[k];
        return v;
      },
    });
    if (coins && coins.gt(0)) {
      run.coins = run.coins.add(coins);
      events.push({ e: 'CHARM_TRIGGER', id, kind: 'coins', amount: coins.toString() });
    }
  }
  // Blood Bank: unused Nudges become ×MULT for the next window.
  if (mods.bloodBank && run.charges.nudge > 0) run.flags.bloodBankX = run.charges.nudge * B.BLOOD_BANK_X;
  else delete run.flags.bloodBankX;
  // Skeleton Key fine print: a random charm crumbles.
  if (run.flags.crumble) {
    delete run.flags.crumble;
    if (run.charms.length) {
      const idx = stream(run.rng, 'shop').int(run.charms.length);
      const [lost] = run.charms.splice(idx, 1);
      events.push({ e: 'CHARM_LOST', id: lost!, by: 'crumble' });
    }
  }
  if ((run.flags.hexImmune ?? 0) > 0) run.flags.hexImmune!--;
}

/** Ends the window: auto-pay if possible, otherwise the player is Consumed (or saved by Last Meal). */
export function windowEnd(state: GameState, mods: Modifiers, events: GameEvent[], early: number): void {
  const run = state.run!;
  runWindowEndCharms(state, mods, events);
  moodWindowEnd(state, mods, events);
  doomWindowEnd(state, mods, events);
  if (run.coins.gte(run.window.tribute)) return payTribute(state, mods, early, events);
  // Last Meal: once per run, the priciest charm is sacrificed instead of you.
  if (mods.lastMeal && !run.flags.lastMealUsed && run.charms.length) {
    const tierOf = (id: string) => B.CHARM_TIER[CHARMS.get(id)?.rarity ?? 'C'];
    let idx = 0;
    run.charms.forEach((id, i) => tierOf(id) > tierOf(run.charms[idx]!) && (idx = i));
    const [charm] = run.charms.splice(idx, 1);
    run.flags.lastMealUsed = 1;
    events.push({ e: 'LAST_MEAL', charm: charm! });
    say(state, 'lastMeal', events, undefined, { force: true });
    run.coins = run.window.tribute;
    return payTribute(state, mods, 0, events);
  }
  consume(state, 'fail', events);
}

export function payNow(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase !== 'ready') return 'not ready';
  if (run.coins.lt(run.window.tribute)) return 'not enough coins';
  windowEnd(state, mods, events, mods.payEarlyDisabled ? 0 : run.window.spinsLeft);
  return undefined;
}

function payTribute(state: GameState, mods: Modifiers, early: number, events: GameEvent[]): void {
  const run = state.run!;
  const meta = state.meta;
  const t = run.window.tribute;
  const bonus = early > 0 && !mods.payEarlyDisabled ? earlyBonus(t, early, mods.payEarlyPct * mods.payEarlyX, run.window.spinsTotal) : D(0);
  run.coins = run.coins.sub(t).add(bonus);
  if (mods.carryX !== 1 && run.coins.gt(0)) run.coins = run.coins.mul(mods.carryX).floor();
  let souls = soulsForTribute(run.window.n, mods);
  run.stats.tributesPaid++;
  meta.lifetime.maxTribute = Math.max(meta.lifetime.maxTribute, run.window.n);
  meta.cycleMaxTribute = Math.max(meta.cycleMaxTribute, run.window.n);
  events.push({
    e: 'TRIBUTE_PAID',
    n: run.window.n,
    souls,
    early,
    earlyBonus: bonus.toString(),
    coinsLeft: run.coins.toString(),
  });
  // A beaten Reckoning: souls, a Rare+ draft, and (after the first Molt) a Relic Shard.
  const collector = reckoningDef(run);
  let beaten: { id: string; souls: number; shard: boolean } | undefined;
  if (collector) {
    run.reck.beaten++;
    meta.lifetime.reckoningsBeaten++;
    if (!meta.lifetime.collectorsBeaten.includes(collector.id)) meta.lifetime.collectorsBeaten.push(collector.id);
    const shard = meta.molts > 0;
    beaten = { id: collector.id, souls: mods.soulsPerReckoning, shard };
    souls += mods.soulsPerReckoning;
    events.push({ e: 'RECKONING_BEATEN', ...beaten });
    say(state, 'reckoningWin', events, undefined, { force: true });
    if (shard) {
      const rng = stream(run.rng, 'events');
      addShard(state, (ids) => rng.pick(ids), events);
    }
  }
  run.soulsPending += souls;
  if (!collector) {
    if (early > 0) say(state, 'payEarly', events);
    else if (run.coins.lt(t.mul(0.1))) say(state, 'barelyPaid', events);
    else say(state, 'paid', events);
  }
  afterTribute(state, mods, early, bonus, souls, events, beaten);
}

export function consume(state: GameState, reason: 'fail' | 'offer', events: GameEvent[]): void {
  const run = state.run!;
  const n = run.window.n;
  const total = Math.max(1, run.soulsPending + consumedBonus(n, run.reck.beaten));
  const meta = state.meta;
  meta.souls += total;
  meta.lifetime.consumed++;
  meta.lifetime.soulsEarned += total;
  meta.cycleMaxTribute = Math.max(meta.cycleMaxTribute, n);
  if (run.flags.endless) meta.lifetime.bestEndless = Math.max(meta.lifetime.bestEndless, n);
  const summary: RunSummary = {
    n,
    paid: run.stats.tributesPaid,
    souls: total,
    bestWin: run.stats.bestWin,
    spins: run.stats.spins,
    reason,
    size: run.size,
    reckonings: run.reck.beaten,
  };
  meta.lastRun = summary;
  meta.history = [summary, ...(meta.history ?? [])].slice(0, B.HISTORY_LENGTH);
  say(state, reason === 'offer' ? 'offer' : 'consumed', events, { n: meta.lifetime.consumed }, { force: true });
  run.phase = 'consumed';
  run.endReason = reason;
  run.bonus = null;
  run.gamble = null;
  run.spin = run.spin ? { ...run.spin, banked: true } : null;
  // Mother's Locket: the leftmost charm comes along unless another is chosen.
  if (computeModifiers(meta, null).locket && run.charms.length) run.flags.locketCharm = 0;
  events.push({ e: 'CONSUMED', souls: total, n, reason });
  tip(state, 'web', events);
}

export function offerSelf(state: GameState, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase === 'consumed') return 'no run';
  if (run.phase === 'bonus') return 'in bonus';
  consume(state, 'offer', events);
  return undefined;
}
